import { Industries, Languages } from '@platform/client';
import { ProfessionalApplicationParameters } from '@platform/client/schemas';
import {
  InlineFormInput,
  MultiSelectCategory,
  MultiSelectCategoryDropdown,
  MultiSelectDropdown,
} from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Beatriz-Scabelli.svg';

type FormValues = {
  yearsOfExperience: number;
  industries: string[];
  languages: string[];
};

export const ExperienceDetailSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, isEditProfile, onSaveExit, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<ProfessionalApplicationParameters>(dataKey);

  const languages = Languages.useGetLanguages();
  const languageOptions =
    languages.isSuccess ?
      languages.data.flatMap((lang) => {
        if (lang.name === 'English') {
          return [];
        }
        return [
          {
            label: lang.name,
          },
        ];
      })
    : [];

  const industriesList = Industries.useGetIndustries();

  const classificationOptions =
    industriesList.isSuccess ?
      Array.from(
        industriesList.data
          .reduce<Map<string, MultiSelectCategory>>((map, industry) => {
            if (!map.has(industry.classification)) {
              map.set(industry.classification, {
                name: industry.classification,
                options: [],
              });
            }
            // eslint-disable-next-line
            map.get(industry.classification)!.options.push(industry.name);
            return map;
          }, new Map())
          .values(),
      )
    : [];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      yearsOfExperience: getApplicationDataByKey('yearsOfExperience'),
      industries: getApplicationDataByKey('industries') || [],
      languages: getApplicationDataByKey('languages') || [],
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      yearsOfExperience: data.yearsOfExperience,
      industries: data.industries,
      languages: data.languages,
    });
    onContinue();
  };

  const onIndustrySelectionChange = (selectedItems: string[]) => {
    setValue('industries', selectedItems);
    if (selectedItems.length > 0) {
      clearErrors('industries');
    }
  };
  const onLanguageSelectionChange = (selectedItems: string[]) => {
    setValue('languages', selectedItems);
  };

  const handleBack = () => {
    updateApplicationData({
      yearsOfExperience: watch('yearsOfExperience'),
      industries: watch('industries'),
      languages: watch('languages'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      isEditProfile={isEditProfile}
      onSaveAndExit={onSaveExit}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='A few more details about your experience'
        description="There's no perfect background - just an opportunity to highlight what makes you, you."
      >
        <InlineFormInput
          label='Years of professional experience'
          inputType='number'
          placeholder='Enter number of years'
          register={() =>
            register('yearsOfExperience', { required: 'Enter years of experience to continue' })
          }
          errors={errors.yearsOfExperience}
          hintText='This is total years of experience, not just in your current role.'
        />
        <MultiSelectCategoryDropdown
          selectedItems={watch('industries') || []}
          onSelectionChange={onIndustrySelectionChange}
          categories={classificationOptions}
          selectionLimit={3}
          label='Select up to 3 industries you have experience in'
          placeholder='Select industries'
          error={errors.industries?.message}
          register={() => {
            return register('industries', {
              required: 'Select at least one industry to continue',
            });
          }}
        />
        <MultiSelectDropdown
          register={() => {
            return register('languages');
          }}
          selectedItems={watch('languages') || []}
          onSelectionChange={onLanguageSelectionChange}
          placeholder='Start typing languages'
          options={languageOptions}
          label='Other than English, what languages are you comfortable working in? (optional)'
          error={errors.languages?.message}
          filterable={true}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
